import { useEffect, useMemo, useState } from 'react';
import { Alert, Input, Modal, Table, message } from 'antd';
import { getModuleDataList } from '../../services/moduleDataApi';
import { createModuleRecordGeneric } from '../../services/adminApi';
import { getActingIdentity } from '../../services/authApi';

function rowsFromResponse(response) {
  const payload = response?.data ?? response;
  if (Array.isArray(payload)) return payload;
  return payload?.data ?? payload?.rows ?? payload?.records ?? payload?.items ?? payload?.list ?? [];
}

function valueFrom(record, ...keys) {
  for (const key of keys) {
    const value = String(key).split('.').reduce((current, part) => current?.[part], record);
    if (value !== undefined && value !== null && value !== '') return value;
  }
  return '';
}

function isActiveAssignment(record) {
  const deleted = String(valueFrom(record, 'isDeleted', 'deleted')).toLowerCase();
  const status = String(valueFrom(record, 'assignmentStatus', 'status')).toLowerCase();
  return deleted !== 'true' && deleted !== '1' && !['removed', 'inactive', 'deleted'].includes(status);
}

function candidateName(record) {
  const explicit = valueFrom(record, 'candidateName', 'candidate_name', 'fullName', 'name');
  if (explicit) return String(explicit);
  return [valueFrom(record, 'firstName', 'first_name'), valueFrom(record, 'lastName', 'last_name')]
    .filter(Boolean)
    .join(' ') || 'Unnamed candidate';
}

function candidateId(record) {
  return String(valueFrom(record, 'candidateId', 'candidate_id', 'candidate._id', 'candidate.id'));
}

function engagementId(record) {
  return String(valueFrom(record, 'workforceEngagementId', 'engagementId', '_id', 'id'));
}

function batchName(record) {
  return String(valueFrom(record, 'batchName', 'name', 'title', 'batch_name'));
}

export default function AddBatchCandidatePopup({ open = true, context = {}, onClose }) {
  const [rows, setRows] = useState([]);
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [assignedByCandidateId, setAssignedByCandidateId] = useState(new Map());

  const batchId = String(context.selectedIds?.[0]
    ?? context.selectedRecords?.[0]?._id
    ?? context.selectedRecords?.[0]?.id
    ?? context.batchId
    ?? '');

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    Promise.resolve()
      .then(() => {
        if (!cancelled) setLoading(true);
        return Promise.all([
          getModuleDataList('onboarding', 200, 0),
          getModuleDataList('batchCandidates', 1000, 0).catch(() => []),
        ]);
      })
      .then(([onboardingResponse, batchCandidatesResponse]) => {
        if (cancelled) return;
        setRows(rowsFromResponse(onboardingResponse));
        setAssignedByCandidateId(new Map(
          rowsFromResponse(batchCandidatesResponse)
            .filter(isActiveAssignment)
            .map((record) => [String(valueFrom(record, 'candidateId', 'candidate_id')), record])
            .filter(([id]) => id),
        ));
      })
      .catch((loadError) => {
        if (!cancelled) setError(loadError.message || 'Unable to load onboarding candidates');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [open]);

  const filteredRows = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return rows;
    return rows.filter((record) => [
      candidateName(record),
      valueFrom(record, 'email', 'candidateEmail', 'candidate_email'),
      valueFrom(record, 'status', 'onboardingStatus', 'onboarding_status'),
    ].some((value) => String(value).toLowerCase().includes(query)));
  }, [rows, search]);

  async function handleAdd() {
    if (!batchId) {
      setError('Select a batch before adding candidates');
      return;
    }
    const selected = rows.filter((record) => selectedKeys.includes(engagementId(record)));
    if (!selected.length) return;
    const alreadyAssigned = selected
      .map((record) => ({ record, assignment: assignedByCandidateId.get(candidateId(record)) }))
      .filter((item) => item.assignment);
    if (alreadyAssigned.length) {
      const names = alreadyAssigned.map(({ record }) => candidateName(record)).join(', ');
      setError(`${names} ${alreadyAssigned.length === 1 ? 'is' : 'are'} already added to a batch. Each candidate can be added to only one batch.`);
      return;
    }

    setSubmitting(true);
    setError('');
    try {
      const { userId } = getActingIdentity();
      const selectedBatchName = batchName(context.selectedRecords?.[0]) || batchName(context);
      await Promise.all(selected.map((record) => createModuleRecordGeneric('batchCandidates', {
        schemaVersion: 1,
        batchId,
        batchName: selectedBatchName,
        candidateId: candidateId(record),
        workforceEngagementId: engagementId(record),
        source: 'ONBOARDING',
        assignmentStatus: 'ASSIGNED',
        assignedAt: new Date().toISOString(),
        assignedBy: userId ?? 0,
        isDeleted: false,
      })));
      message.success(`${selected.length} candidate${selected.length === 1 ? '' : 's'} added to the batch`);
      onClose?.({ refresh: true });
    } catch (submitError) {
      setError(submitError.message || 'Unable to add candidates to the batch');
    } finally {
      setSubmitting(false);
    }
  }

  const columns = [
    { title: 'Candidate', key: 'candidate', render: (_, record) => candidateName(record) },
    {
      title: 'Email',
      key: 'email',
      render: (_, record) => valueFrom(record, 'email', 'candidateEmail', 'candidate_email') || '—',
    },
    {
      title: 'Onboarding status',
      key: 'status',
      render: (_, record) => valueFrom(record, 'status', 'onboardingStatus', 'onboarding_status') || '—',
    },
  ];

  return (
    <Modal
      open={open}
      title="Add Candidate to Batch"
      okText="Add"
      onOk={handleAdd}
      onCancel={() => onClose?.()}
      okButtonProps={{ disabled: selectedKeys.length === 0 || !batchId, loading: submitting }}
      cancelButtonProps={{ disabled: submitting }}
      closable={!submitting}
      maskClosable={false}
      width={760}
      centered
    >
      {error && <Alert type="error" showIcon message={error} style={{ marginBottom: 12 }} />}
      <Input.Search
        allowClear
        placeholder="Search onboarding candidates"
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        style={{ marginBottom: 12 }}
      />
      <Table
        rowKey={(record) => engagementId(record)}
        columns={columns}
        dataSource={filteredRows}
        loading={loading}
        size="small"
        pagination={{ pageSize: 10, showSizeChanger: false }}
        rowSelection={{
          selectedRowKeys: selectedKeys,
          onChange: setSelectedKeys,
          getCheckboxProps: (record) => {
            const assignment = assignedByCandidateId.get(candidateId(record));
            return {
              disabled: !candidateId(record) || !engagementId(record) || Boolean(assignment),
              title: assignment ? 'Candidate is already added to a batch' : undefined,
            };
          },
        }}
        locale={{ emptyText: loading ? 'Loading candidates…' : 'No onboarding candidates found' }}
      />
    </Modal>
  );
}
